/* ============================================================================
   site-access.js - signed-in gate for blocked accounts / IPs
   ============================================================================ */

import { auth, functions, isConfigured } from "./firebase-config.js?v=20261007m";
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { signOut } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js";
import { clearSignedOutCaches } from "./signed-out-caches.js?v=20261007m";
import { rememberAccessBlock } from "./access-block-copy.js?v=20261007m";

let lastCheck = { at: 0, ok: true };

const ACCESS_SESSION_KEY = "ccj_access_ok";

/** Drop in-memory + session hints (call on sign-out). */
export function resetSiteAccessCache() {
  lastCheck = { at: 0, ok: true };
  try {
    sessionStorage.removeItem(ACCESS_SESSION_KEY);
  } catch (_) {}
}

export async function checkSiteAccess({ force = false } = {}) {
  if (!isConfigured || !functions || !auth?.currentUser) {
    return { ok: true };
  }

  const now = Date.now();
  if (!force && lastCheck.ok && now - lastCheck.at < 60_000) {
    return { ok: true, cached: true };
  }

  try {
    const fn = httpsCallable(functions, "checkSiteAccess");
    const { data } = await fn({});
    const payload = data || { ok: true };
    lastCheck = { at: now, ok: payload.ok !== false };
    return payload;
  } catch (err) {
    const code = String(err?.code || "");
    const message = String(err?.message || "");
    if (code.includes("permission-denied") || /blocked/i.test(message)) {
      lastCheck = { at: now, ok: false };
      const reason = /network|ip/i.test(message) ? "ip" : "account";
      return { ok: false, reason, message: message || "Access blocked." };
    }
    // Fail open on transient network errors so one blip does not lock everyone out.
    console.warn("[CookieCodeJar] checkSiteAccess:", message || err);
    return { ok: true, softFail: true };
  }
}

export async function enforceSiteAccessOrExit() {
  let force = false;
  try {
    // First signed-in page in this tab: verify with the server. Turbo hops reuse cache.
    force = !sessionStorage.getItem(ACCESS_SESSION_KEY);
  } catch (_) {
    force = true;
  }

  const result = await checkSiteAccess({ force });
  if (result?.ok !== false) {
    try {
      sessionStorage.setItem(ACCESS_SESSION_KEY, "1");
    } catch (_) {}
    return true;
  }

  const reason = result?.reason === "ip" ? "ip" : "account";
  rememberAccessBlock(reason);
  resetSiteAccessCache();

  try {
    clearSignedOutCaches();
    if (auth?.currentUser) await signOut(auth);
  } catch (_) {}

  window.location.replace(`home.html?blocked=${reason}`);
  return false;
}
